import React, { useCallback, useContext, useState } from 'react';
import styled from 'styled-components';
import { coreapi, onboardingapi } from '../../api';
import { useAuthToken, useToaster } from '../../hooks';
import { RegistrationStore, updateRegistration } from '../../context';
import { Button, Group, ResponsiveMargin, Select } from '../';
import { removeLinkedPlaidAccount } from '../../util/plaid.util';
import { PlaidAccountBase } from '../../pages/financial/FinancialFields.types';
import { AccountUnlinkModal } from './AccountUnlinkModal';

export const PendingAccount = styled.span`
  box-sizing: border-box;
  text-align: center;
  font-size: 14px;
  color: #d67300;
  background: #fefbe6;
  box-shadow: 0px 1px 3px rgba(214, 115, 0, 0.2);
  border-radius: 8px;
  margin-right: 8px;
  padding: 10px 5px;
  box-sizing: border-box;
  width: auto;
  height: 30px;
`;

export const LinkedAccount = styled.span`
  background: #dfffe8;
  color: #21a446;
  box-shadow: 0px 1px 3px rgba(33, 164, 70, 0.2);
  border-radius: 8px;
  padding: 10px 5px;
  box-sizing: border-box;
`;

export const PlaidAccountNumbers = (props: {
  accounts: PlaidAccountBase[];
  updatingAccounts: boolean;
  hasManualSecondaryAccount: boolean;
  updateAccountNumbers: () => Promise<void>;
  setUpdatingAccounts: React.Dispatch<React.SetStateAction<boolean>>;
  setLinkToken: React.Dispatch<React.SetStateAction<string | null>>;
}) => {
  const { authToken } = useAuthToken();
  const { toaster, toast } = useToaster();
  const {
    accounts,
    updatingAccounts,
    hasManualSecondaryAccount,
    updateAccountNumbers,
    setUpdatingAccounts,
    setLinkToken,
  } = props;
  const {
    dispatch,
    state: { registration },
  } = useContext(RegistrationStore);
  const [showPrimaryRow, setShowPrimaryRow] = useState('inherit');
  const [showSecondaryRow, setShowSecondaryRow] = useState('inherit');
  const [showPrimaryModal, setShowPrimaryModal] = useState(false);
  const [showSecondaryModal, setShowSecondaryModal] = useState(false);

  const updateAccountIsUsedFor = useCallback(
    async e => {
      if (e.target.value === '') return;
      let value = e.target.value;
      try {
        // Update secondary account purpose
        setShowSecondaryRow('none');
        setUpdatingAccounts(true);

        const r = await coreapi.put(authToken!, '/team/registration', {
          secondary_bank_account_purpose: value,
        });
        dispatch(updateRegistration(r));

        await updateAccountNumbers();

        setShowSecondaryRow('inherit');
        setUpdatingAccounts(false);
        toaster(toast.success('Account Purpose Updated'));
      } catch (error) {
        setUpdatingAccounts(false);
        toaster(toast.error(error?.message));
      }
    },
    [
      setShowSecondaryRow,
      updateAccountNumbers,
      setUpdatingAccounts,
      toaster,
      toast,
      dispatch,
      authToken,
    ],
  );

  const lastPlaidAccountIdx = hasManualSecondaryAccount
    ? -1
    : accounts.length - 1;

  const AccountList = accounts.map((account, key) => {
    const plaidId = account.plaid_token_id;
    const setShowAccountRow =
      account.account_type === 'primary'
        ? setShowPrimaryRow
        : setShowSecondaryRow;

    const removeAccount = async () => {
      try {
        setShowAccountRow('none');
        setUpdatingAccounts(true);
        const { registrationChanges } = await removeLinkedPlaidAccount(
          authToken,
          plaidId,
        );
        const { token } = await onboardingapi.get(authToken!, '/banking/verify');
        localStorage.setItem(`linkToken.${registration!.merchant_id}`, token);
        setLinkToken(token);
        await updateAccountNumbers();
        setShowAccountRow('inherit');
        setUpdatingAccounts(false);
        // required after server registration change to ensure Registration.Context stays in sync
        dispatch(
          updateRegistration({ ...registration!, ...registrationChanges }),
        );
        return true;
      } catch (err) {
        setUpdatingAccounts(false);
        setShowAccountRow('inherit');
        return false;
      }
    };

    return (
      <Group
        direction={'row'}
        className={'plaid-account-row'}
        style={{
          marginBottom: '15px',
          display: key === 0 ? showPrimaryRow : showSecondaryRow,
        }}
        key={key.toString()}
      >
        <AccountUnlinkModal
          isOpen={
            account.account_type === 'primary'
              ? showPrimaryModal
              : showSecondaryModal
          }
          showModal={
            account.account_type === 'primary'
              ? setShowPrimaryModal
              : setShowSecondaryModal
          }
          maskedAccount={account.mask!}
          removeAccount={removeAccount}
        />
        <Group
          direction={'row'}
          className={'plaid-account-display'}
          style={{ width: '300px' }}
        >
          <ResponsiveMargin
            desktop={'17px 24px 5px 0'}
            mobile={'10px 16px 5px 5px'}
          >
            <strong style={{ fontWeight: 'bolder' }}>
              {account.name} **{account.mask}
            </strong>
          </ResponsiveMargin>
          <ResponsiveMargin desktop={'17px 0 5px'} mobile={'10px 0 5px'}>
            {account.account_type === null ? (
              <PendingAccount>*** Pending</PendingAccount>
            ) : (
              <LinkedAccount>Linked</LinkedAccount>
            )}
          </ResponsiveMargin>
        </Group>
        <Group
          direction={'row'}
          className={'plaid-account-actions'}
          style={{ marginLeft: '100px' }}
        >
          <Group
            direction={'column'}
            style={{ marginRight: '10px', width: '200px' }}
          >
            {account.account_type === 'primary' && (
              <Select>
                <option>Primary</option>
              </Select>
            )}
            {account.account_type === 'secondary' && (
              <Select
                defaultValue={
                  registration?.secondary_bank_account_purpose ?? ''
                }
                onChange={updateAccountIsUsedFor}
              >
                <option value="">Account is used for</option>
                <option value="discount">Discount</option>
                <option value="fees">Fees</option>
                <option value="credits">Credits</option>
                <option value="chargebacks">Chargebacks</option>
              </Select>
            )}
          </Group>
          <Group direction={'column'}>
            {key === lastPlaidAccountIdx && (
              <Button
                variant="plain"
                motif="destructive"
                id={`delete-linked-plaid-account-${key.toString()}`}
                data-testid={`delete-linked-plaid-account-${key.toString()}-btn`}
                size="normal"
                icon="fas fa-times"
                style={{
                  display: 'inline-block',
                  marginBottom: 0,
                  marginLeft: 'auto',
                  marginTop: '17px',
                  color: '#E30029',
                }}
                disabled={updatingAccounts}
                onClick={() => {
                  account.account_type === 'primary'
                    ? setShowPrimaryModal(true)
                    : setShowSecondaryModal(true);
                }}
              ></Button>
            )}
          </Group>
        </Group>
      </Group>
    );
  });
  return <Group direction={'column'}>{AccountList}</Group>;
};
